/**
 * File Viewer Manager
 * Handles file preview modal navigation and resizing.
 */

var FileViewerManager = (function() {
  'use strict';

  var initialized = false;
  var activeRoot = null;
  var resizeTimer = null;

  function getModal(root) {
    if (!root) return null;

    var modalId = root.getAttribute('data-file-viewer-modal-id');
    if (modalId) {
      return document.getElementById(modalId);
    }

    return root.querySelector('.file-viewer-modal');
  }

  function getItems(root) {
    if (!root) return [];
    return Array.prototype.slice.call(root.querySelectorAll('[data-file-viewer-open]'));
  }

  function getIndex(root) {
    var value = root ? root.getAttribute('data-file-viewer-active-index') : null;
    var parsed = parseInt(value || '0', 10);
    return isNaN(parsed) ? 0 : parsed;
  }

  function setIndex(root, index) {
    if (!root) return;
    root.setAttribute('data-file-viewer-active-index', String(index));
  }

  function getViewportWidth() {
    return window.innerWidth || document.documentElement.clientWidth || 1280;
  }

  function isSmallViewport() {
    return getViewportWidth() < 1024;
  }

  function getText(item, attribute) {
    return item ? (item.getAttribute(attribute) || '') : '';
  }

  function clearNode(node) {
    FilePreviewRenderer.clearNode(node);
  }

  function getPreviewShell(modal) {
    return modal ? modal.querySelector('[data-file-viewer-preview-shell]') : null;
  }

  function getBody(modal) {
    return modal ? modal.querySelector('[data-file-viewer-body]') : null;
  }

  function setMeasuredWidth(shell, measuredWidth) {
    if (!shell) return;
    shell.setAttribute('data-file-viewer-measured-width', String(measuredWidth || 0));
  }

  function getMeasuredWidth(shell) {
    if (!shell) return 0;

    var value = parseInt(shell.getAttribute('data-file-viewer-measured-width') || '0', 10);
    return isNaN(value) ? 0 : value;
  }

  function setRenderToken(shell) {
    if (!shell) return '';

    var token = String(Date.now()) + '-' + Math.random().toString(16).slice(2);
    shell.setAttribute('data-file-viewer-render-token', token);
    return token;
  }

  function isCurrentRender(shell, token) {
    return !!shell && shell.getAttribute('data-file-viewer-render-token') === token;
  }

  function updateMeta(root, modal, item, index, count, mode) {
    var title = modal ? modal.querySelector('[data-file-viewer-title]') : null;
    var name = modal ? modal.querySelector('[data-file-viewer-name]') : null;
    var url = modal ? modal.querySelector('[data-file-viewer-url]') : null;
    var position = modal ? modal.querySelector('[data-file-viewer-position]') : null;
    var download = modal ? modal.querySelector('[data-file-viewer-download]') : null;
    var prev = modal ? modal.querySelector('[data-file-viewer-prev]') : null;
    var next = modal ? modal.querySelector('[data-file-viewer-next]') : null;
    var fileName = getText(item, 'data-file-viewer-name');
    var fileUrl = getText(item, 'data-file-viewer-url');

    if (title) title.textContent = fileName || 'File preview';
    if (name) name.textContent = fileName || 'Select a file';
    if (url) url.textContent = fileUrl;
    if (position) position.textContent = count > 0 ? (index + 1) + ' of ' + count : '';
    if (download) {
      download.classList.remove('hidden');
      if (fileUrl) {
        download.setAttribute('href', fileUrl);
      }

      if (fileName) {
        var safeName = (fileName || '').replace(/[^a-zA-Z0-9._\- ]/g, '_').replace(/\.{2,}/g, '.');
        download.setAttribute('download', safeName);
      }
    }

    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= count - 1;

    if (root) {
      root.setAttribute('data-file-viewer-active-name', fileName || '');
      root.setAttribute('data-file-viewer-active-url', fileUrl || '');
    }
  }

  function syncWidth(modal, shell, measuredWidth) {
    if (!modal || !shell) return;
    setMeasuredWidth(shell, measuredWidth);

    if (isSmallViewport()) {
      shell.style.width = '100%';
      modal.style.setProperty('--file-viewer-width', (getViewportWidth() - 32) + 'px');
      return;
    }

    var actions = modal.querySelector('[data-file-viewer-actions-column]');
    var actionsWidth = actions ? actions.offsetWidth : 288;
    var viewportWidth = getViewportWidth();
    var maxPreviewWidth = Math.max(240, viewportWidth - 32 - actionsWidth - 48);
    var previewWidth = Math.max(240, Math.min(measuredWidth || 960, maxPreviewWidth));

    shell.style.width = previewWidth + 'px';
    modal.style.setProperty('--file-viewer-width', (previewWidth + actionsWidth + 48) + 'px');
  }

  function syncLayout(modal) {
    if (!modal) return;

    var body = getBody(modal);
    var preview = modal.querySelector('[data-file-viewer-preview-column]');
    var actions = modal.querySelector('[data-file-viewer-actions-column]');

    if (!body || !preview || !actions) return;

    if (isSmallViewport()) {
      body.style.flexDirection = 'column';
      body.style.alignItems = '';
      preview.style.flex = '1 1 auto';
      preview.style.minWidth = '0';
      actions.style.width = '100%';
      actions.style.flex = '1 1 auto';
      actions.style.maxWidth = '100%';
      return;
    }

    body.style.flexDirection = 'row';
    body.style.alignItems = 'flex-start';
    preview.style.flex = '1 1 0%';
    preview.style.minWidth = '0';
    actions.style.width = '18rem';
    actions.style.flex = '0 0 18rem';
    actions.style.maxWidth = '18rem';
  }

  function refreshWidthForActiveFile() {
    if (!activeRoot) return;

    var modal = getModal(activeRoot);
    var shell = getPreviewShell(modal);
    var preview = shell ? shell.querySelector('img, iframe') : null;
    var measuredWidth = getMeasuredWidth(shell);

    if (!modal || !shell) return;

    syncLayout(modal);

    if (preview && preview.tagName === 'IMG') {
      measuredWidth = preview.naturalWidth || preview.width || measuredWidth || 640;
    } else if (!measuredWidth) {
      measuredWidth = 960;
    }

    syncWidth(modal, shell, measuredWidth);
  }

  function renderFallback(modal, shell, name, message) {
    FilePreviewRenderer.renderFallback(shell, name, message);
    syncWidth(modal, shell, 520);
  }

  function renderDocx(modal, shell, name, url) {
    var renderToken = setRenderToken(shell);
    syncWidth(modal, shell, 960);

    FilePreviewRenderer.renderDocx(shell, name, url, {
      renderToken: renderToken,
      onLoad: function(iframe) {
        if (!isCurrentRender(shell, renderToken)) return;

        var width = 960;

        try {
          var doc = iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document);
          if (doc) {
            width = Math.max(
              doc.documentElement ? doc.documentElement.scrollWidth : 0,
              doc.body ? doc.body.scrollWidth : 0,
              doc.documentElement ? doc.documentElement.offsetWidth : 0,
              doc.body ? doc.body.offsetWidth : 0,
              960
            );
          }
        } catch (error) {
          width = 960;
        }

        syncWidth(modal, shell, width);
      },
      onError: function() {
        if (!isCurrentRender(shell, renderToken)) return;
        renderFallback(modal, shell, name, 'Preview for this document could not be loaded.');
      }
    });
  }

  function renderImage(modal, shell, name, url) {
    var img = FilePreviewRenderer.renderImage(shell, name, url);
    img.addEventListener('load', function() {
      syncWidth(modal, shell, img.naturalWidth || img.width || 640);
    });

    if (img.complete) {
      syncWidth(modal, shell, img.naturalWidth || img.width || 640);
    } else {
      syncWidth(modal, shell, 640);
    }
  }

  function renderDocument(modal, shell, name, url) {
    var iframe = FilePreviewRenderer.renderDocument(shell, name, url);
    iframe.addEventListener('load', function() {
      var width = 960;

      try {
        var doc = iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document);
        if (doc) {
          width = Math.max(
            doc.documentElement ? doc.documentElement.scrollWidth : 0,
            doc.body ? doc.body.scrollWidth : 0,
            doc.documentElement ? doc.documentElement.offsetWidth : 0,
            doc.body ? doc.body.offsetWidth : 0,
            960
          );
        }
      } catch (error) {
        width = 960;
      }

      syncWidth(modal, shell, width);
    });

    syncWidth(modal, shell, 960);
  }

  function renderActiveFile(root, modal) {
    var items = getItems(root);
    var shell = modal ? modal.querySelector('[data-file-viewer-preview-shell]') : null;

    if (!items.length || !shell) return;

    var index = getIndex(root);
    if (index < 0) index = 0;
    if (index >= items.length) index = items.length - 1;
    setIndex(root, index);

    var item = items[index];
    var name = getText(item, 'data-file-viewer-name');
    var url = getText(item, 'data-file-viewer-url');
    var mode = getText(item, 'data-file-viewer-preview-mode');

    updateMeta(root, modal, item, index, items.length, mode);

    if (mode === 'image') {
      renderImage(modal, shell, name, url);
      return;
    }

    if (mode === 'docx') {
      renderDocx(modal, shell, name, url);
      return;
    }

    if (mode === 'document') {
      renderDocument(modal, shell, name, url);
      return;
    }

    renderFallback(modal, shell, name);
  }

  function openFromTrigger(trigger) {
    var root = trigger ? trigger.closest('[data-file-viewer-root]') : null;
    var modal = getModal(root);
    var items = getItems(root);
    var index = items.indexOf(trigger);

    if (!root || !modal || !window.ModalManager || typeof window.ModalManager.open !== 'function') {
      return;
    }

    if (index < 0) {
      index = 0;
    }

    activeRoot = root;
    setIndex(root, index);

    if (!modal.classList.contains('hidden') && modal.classList.contains('block')) {
      renderActiveFile(root, modal);
      return;
    }

    window.ModalManager.open(modal, trigger);
  }

  function goToRelativeIndex(root, delta) {
    var modal = getModal(root);
    var items = getItems(root);
    var index = getIndex(root) + delta;

    if (!items.length) return;
    if (index < 0) index = 0;
    if (index >= items.length) index = items.length - 1;

    setIndex(root, index);
    renderActiveFile(root, modal);
  }

  function handleClick(event) {
    var openTrigger = event.target.closest('[data-file-viewer-open]');
    if (openTrigger) {
      event.preventDefault();
      openFromTrigger(openTrigger);
      return;
    }

    var prevTrigger = event.target.closest('[data-file-viewer-prev]');
    if (prevTrigger) {
      event.preventDefault();
      goToRelativeIndex(prevTrigger.closest('[data-file-viewer-root]'), -1);
      return;
    }

    var nextTrigger = event.target.closest('[data-file-viewer-next]');
    if (nextTrigger) {
      event.preventDefault();
      goToRelativeIndex(nextTrigger.closest('[data-file-viewer-root]'), 1);
      return;
    }

  }

  function handleKeydown(event) {
    var openTrigger = event.target.closest('[data-file-viewer-open]');
    if (openTrigger && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      openFromTrigger(openTrigger);
      return;
    }

    if (!activeRoot) return;

    if (event.target && /INPUT|TEXTAREA|SELECT/.test(event.target.tagName)) {
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goToRelativeIndex(activeRoot, -1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      goToRelativeIndex(activeRoot, 1);
    }
  }

  function handleModalOpened(event) {
    var modalId = event.detail && event.detail.modalId;
    var trigger = event.detail && event.detail.trigger;
    var modal = modalId ? document.getElementById(modalId) : null;
    var root = trigger ? trigger.closest('[data-file-viewer-root]') : null;

    if (!modal || !root) return;

    activeRoot = root;
    root.setAttribute('data-file-viewer-is-open', 'true');
    syncLayout(modal);
    renderActiveFile(root, modal);
  }

  function handleModalClosed(event) {
    var modalId = event.detail && event.detail.modalId;
    var modal = modalId ? document.getElementById(modalId) : null;
    var root = modal ? modal.closest('[data-file-viewer-root]') : activeRoot;

    if (root) {
      root.setAttribute('data-file-viewer-is-open', 'false');
    }

    activeRoot = null;
  }

  function handleResize() {
    if (!activeRoot) return;

    if (resizeTimer) {
      clearTimeout(resizeTimer);
    }

    resizeTimer = setTimeout(function() {
      if (activeRoot) {
        syncLayout(getModal(activeRoot));
      }
      refreshWidthForActiveFile();
      resizeTimer = null;
    }, 80);
  }

  function init() {
    if (initialized) return;

    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKeydown);
    document.addEventListener('modal:opened', handleModalOpened);
    document.addEventListener('modal:closed', handleModalClosed);
    window.addEventListener('resize', handleResize);
    document.addEventListener('turbo:load', function() {
      activeRoot = null;
    });
    document.addEventListener('turbolinks:load', function() {
      activeRoot = null;
    });

    initialized = true;
  }

  return {
    init: init
  };
})();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', FileViewerManager.init);
} else {
  FileViewerManager.init();
}

window.FileViewerManager = FileViewerManager;
